import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getOrderedQuantityError } from '@/services/apis/errors/ordered-quantity-error';
import { useUpdateLotSizingPurchaseOrderLines } from '@/services/apis/mutations/lot-sizing/use-update-lot-sizing-purchase-order-lines';
import { useLotSizingPurchaseOrderDraft } from './lot-sizing-purchase-order-draft-context';

type LotSizingWithPurchaseOrderSaveProps = {
  lotSizingId: string;
  disabled: boolean;
};

export function LotSizingWithPurchaseOrderSave({
  lotSizingId,
  disabled,
}: LotSizingWithPurchaseOrderSaveProps) {
  const { t } = useTranslation('common');
  const { getSavePayload, hasChanges, hasQuantityErrors, resetDraft } =
    useLotSizingPurchaseOrderDraft();
  const { mutateAsync, isPending } = useUpdateLotSizingPurchaseOrderLines();

  const handleClick = async () => {
    if (hasQuantityErrors) {
      return;
    }
    const lines = getSavePayload();

    if (lines.length === 0) {
      resetDraft();
      return;
    }

    try {
      await mutateAsync({
        lotSizingId,
        lines,
      });
      resetDraft();
    } catch (error) {
      const data = axios.isAxiosError(error) ? error.response?.data : undefined;
      const description =
        getOrderedQuantityError(error, t) ||
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.detail === 'string' && data.detail) ||
        t('error');

      toast.error(t('error'), { description });
    }
  };

  return (
    <Button
      disabled={disabled || !hasChanges || hasQuantityErrors || isPending}
      onClick={handleClick}
      type='button'
    >
      {t('save')}
    </Button>
  );
}
